import { Button, Icons, Stack, Typography } from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Outlet, useMatches } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { JournalDtoTopic, PatientBasicsDto } from '../../api/generated/model';
import {
  getGetPatientBasicsByIdQueryOptions,
  getGetPatientByIdQueryKey,
  useGenerateCaseNumber,
  useGetPatientByIdSuspense,
} from '../../api/generated/patient-controller/patient-controller.ts';
import { queryClient } from '../../api/queryClient.ts';
import { Breadcrumbs } from '../../components/Breadcrumbs.tsx';
import { ContentSection } from '../../components/ContentSection.tsx';
import { ErrorBoundary } from '../../components/errors/ErrorBoundary.tsx';
import { MotionDivider } from '../../components/MotionDivider.tsx';
import { StickyContainer } from '../../components/StickyContainer.tsx';
import { formatDateYear } from '../../utils/date.ts';
import { TB_CASE_DOCUMENT_CATEGORIES } from '../../utils/document.ts';
import { AgeAndAgeCategoryAtCreation } from '../components/AgeAndAgeCategoryAtCreation.tsx';
import { CreateJournalEntryDialog } from '../components/journal/CreateJournalEntryDialog.tsx';
import { CreateDocumentDialog } from './components/CreateDocumentDialog.tsx';
import { CreateLetterDialog } from './components/CreateLetterDialog.tsx';
import { PatientsSideNavigation } from './components/PatientsSideNavigation.tsx';
import { UploadDocumentDialog } from './components/UploadDocumentDialog.tsx';

export const Route = createFileRoute('/patients/$patientId')({
  component: Patient,
  loader: async ({ params: { patientId } }) =>
    await queryClient.ensureQueryData(getGetPatientBasicsByIdQueryOptions(patientId)),
  staticData: ({ loaderData: patient }: { loaderData: PatientBasicsDto }) => {
    return { title: `${patient.firstName} ${patient.lastName}` };
  },
});

function Patient() {
  const { patientId } = Route.useParams();
  const { data: patient } = useGetPatientByIdSuspense(patientId);
  const [isJournalDialogOpen, setIsJournalDialogOpen] = useState(false);
  const [isLetterDialogOpen, setIsLetterDialogOpen] = useState(false);
  const [isCreateDocumentDialogOpen, setIsCreateDocumentDialogOpen] = useState(false);
  const [isUploadDocumentDialogOpen, setIsUploadDocumentDialogOpen] = useState(false);
  const contentSectionRef = useRef(null);
  const queryClient = useQueryClient();

  // Determine the journal topic based on the current match's pathname or route id.
  const matches = useMatches();
  const currentMatch = matches[matches.length - 1];
  const isDocumentRoute = currentMatch.routeId === '/patients/$patientId/document';
  const preselectedTopic: JournalDtoTopic = (() => {
    switch (currentMatch.routeId) {
      case '/patients/$patientId/anamnesis':
        return JournalDtoTopic.ANAMNESIS;
      case '/patients/$patientId/therapy':
        return JournalDtoTopic.THERAPY;
      case '/patients/$patientId/diagnostics':
        return JournalDtoTopic.DIAGNOSTICS;
      case '/patients/$patientId':
      case '/patients/$patientId/':
        return JournalDtoTopic.PERSONAL_DETAILS;
      case '/patients/$patientId/network':
        return JournalDtoTopic.NETWORK;
      default:
        return JournalDtoTopic.NONE;
    }
  })();

  const generateCaseNumberMutation = useGenerateCaseNumber({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetPatientByIdQueryKey(patientId) });
      },
      onError: () => {
        toast.error('Beim Aufnehmen als Indexpatient ist ein Fehler aufgetreten');
      },
    },
  });

  return (
    <Stack direction="row" gap={6}>
      <PatientsSideNavigation patientId={patientId} />
      <ContentSection ref={contentSectionRef} flex={1} gap={2}>
        <StickyContainer>
          <Stack direction={'row'} justifyContent={'space-between'} alignItems={'start'}>
            <Stack>
              <Typography level="h2">
                {[patient.caseNumber, patient.lastName, patient.firstName, formatDateYear(patient.birthDate)]
                  .filter(Boolean)
                  .join(' ')}
              </Typography>
              <Typography level={'body-lg'}>
                Alter: <AgeAndAgeCategoryAtCreation patient={patient} />
              </Typography>
              <Breadcrumbs />
            </Stack>
            <Stack direction={'row'} gap={2}>
              {isDocumentRoute ? (
                <>
                  <Button
                    color={'neutral'}
                    endDecorator={<Icons.UploadFile />}
                    onClick={() => setIsUploadDocumentDialogOpen(true)}>
                    Dokument hochladen
                  </Button>
                  <Button
                    color={'neutral'}
                    endDecorator={<Icons.Add />}
                    onClick={() => setIsCreateDocumentDialogOpen(true)}>
                    Dokument erstellen
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    color={'neutral'}
                    onClick={() => setIsLetterDialogOpen(true)}
                    endDecorator={<Icons.Description />}>
                    Bericht ausfüllen
                  </Button>
                  <Button color={'neutral'} onClick={() => setIsJournalDialogOpen(true)} endDecorator={<Icons.Notes />}>
                    Notiz erfassen
                  </Button>
                </>
              )}
              {!patient.caseNumber && (
                <Button
                  color={'neutral'}
                  onClick={() => generateCaseNumberMutation.mutate(patient)}
                  loading={generateCaseNumberMutation.isPending}>
                  Als Indexpatient aufnehmen
                </Button>
              )}
            </Stack>
          </Stack>
          <MotionDivider contentSectionRef={contentSectionRef} />
        </StickyContainer>
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </ContentSection>
      <CreateJournalEntryDialog
        patientId={patientId}
        preselectedTopic={preselectedTopic}
        open={isJournalDialogOpen}
        onClose={() => setIsJournalDialogOpen(false)}
        disabledTopics={[JournalDtoTopic.TRACING_CONTACT]}
      />
      <CreateLetterDialog
        patientId={patientId}
        open={isLetterDialogOpen}
        onClose={() => setIsLetterDialogOpen(false)}
      />
      <CreateDocumentDialog
        patientId={patientId}
        categories={TB_CASE_DOCUMENT_CATEGORIES}
        open={isCreateDocumentDialogOpen}
        onClose={() => setIsCreateDocumentDialogOpen(false)}
      />
      <UploadDocumentDialog
        patientId={patientId}
        categories={TB_CASE_DOCUMENT_CATEGORIES}
        open={isUploadDocumentDialogOpen}
        onClose={() => setIsUploadDocumentDialogOpen(false)}
      />
    </Stack>
  );
}
